import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Button, Input, Modal, Typography, Card, Divider, message } from 'antd'
import { Scanner } from '@yudiel/react-qr-scanner'
import './MafEvents.css'
import { getEvents, eventRedeemCode } from 'src/api/event'
import qr from 'src/assets/images/qr.png'

const MafEvents: React.FC = () => {
  const [qrVisible, setQrVisible] = useState(false)
  const [scannedCode, setScannedCode] = useState('')
  const [manualCode, setManualCode] = useState('')
  const [confirmModal, setConfirmModal] = useState(false)
  const [successModal, setSuccessModal] = useState(false)
  const [errorModal, setErrorModal] = useState({ visible: false, message: '' })
  const [cameraReady, setCameraReady] = useState(false)
  const [eventId, setEventId] = useState('')
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user'>(
    'environment'
  )
  const scanCountRef = useRef(0)

  const handleScan = useCallback((result: any) => {
    if (result && scanCountRef.current === 0) {
      scanCountRef.current += 1
      setScannedCode(result ? result[0].rawValue : '')
      setQrVisible(false)
      setConfirmModal(true)
      setTimeout(() => (scanCountRef.current = 0), 2000)
    }
  }, [])

  const handleError = useCallback((err: any) => {
    console.error('QR Scanner Error:', err)
    message.error('Camera error: ' + err.message)
  }, [])

  const handleRedeem = async () => {
    setConfirmModal(false)
    const code = scannedCode || manualCode
    const result = await eventRedeemCode(eventId, code)

    if (result.success) {
      setSuccessModal(true)
    } else {
      let errorMessage = 'Something went wrong!'
      try {
        const metaMsg = result?.error?.meta_data?.message
        if (metaMsg?.includes('already used')) {
          errorMessage = 'This redeem code has already been used.'
        } else if (metaMsg?.includes('not found')) {
          errorMessage = 'This redeem code was not found.'
        } else if (metaMsg) {
          errorMessage = metaMsg
        }
      } catch (_) {
        console.log('')
      }
      setErrorModal({ visible: true, message: errorMessage })
    }
  }

  const startCamera = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      message.error('Camera not supported in this browser')
      return
    }
    setCameraReady(false)
    setQrVisible(true)
    scanCountRef.current = 0

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: cameraFacing } })
      .then((stream) => {
        setCameraReady(true)
        stream.getTracks().forEach((track) => track.stop())
      })
      .catch((err) => {
        console.error('Camera access denied:', err)
        message.error('Camera access denied. Please allow camera permissions.')
        setQrVisible(false)
      })
  }

  const stopCamera = () => {
    setQrVisible(false)
    setCameraReady(false)
  }

  const toggleCamera = () => {
    setCameraFacing((prev) => (prev === 'environment' ? 'user' : 'environment'))
  }

  const fetchEventId = async () => {
    const result = await getEvents()
    setEventId(result.data[0].id)
  }

  useEffect(() => {
    fetchEventId()
  }, [])

  return (
    <>
      <div
        style={{
          display: window.innerWidth <= 480 ? 'none' : 'flex',
        }}
      >
        Redeem Product List
      </div>
      <Divider />

      <div className="redeem-container">
        <Card className="redeem-card">
          <Typography.Title level={4} className="redeem-title">
            Scan & Redeem Prizes
          </Typography.Title>

          {!qrVisible ? (
            <div className="qr-section">
              <div className="qr-box" onClick={startCamera}>
                <img src={qr} className="qr-image" alt="QR" />
                <Typography.Title level={4} className="redeem-title">
                  Click and scan QR code
                </Typography.Title>
              </div>

              <div className="or-text">OR</div>

              <Input
                placeholder="Enter QR code"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                className="input-code"
              />
              <Button
                className="submit-btn"
                onClick={() => {
                  setScannedCode(manualCode)
                  setConfirmModal(true)
                }}
                disabled={!manualCode}
              >
                Submit
              </Button>
            </div>
          ) : (
            <div className="camera-view">
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '400px',
                  margin: '0 auto',
                }}
              >
                <Scanner
                  onScan={handleScan}
                  onError={handleError}
                  constraints={{
                    facingMode: { ideal: cameraFacing },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                  }}
                  styles={{
                    container: { width: '100%' },
                    video: { width: '100%', borderRadius: 8 },
                  }}
                />

                {cameraReady && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '200px',
                      height: '200px',
                      border: '2px solid #1890ff',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      pointerEvents: 'none',
                    }}
                  ></div>
                )}
              </div>

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Button onClick={toggleCamera}>
                  Switch to {cameraFacing === 'environment' ? 'Front' : 'Back'}{' '}
                  Camera
                </Button>
              </div>

              <div
                style={{
                  textAlign: 'center',
                  marginTop: '16px',
                  color: '#666',
                  fontSize: '14px',
                }}
              >
                {cameraReady
                  ? 'Point camera at QR code'
                  : 'Initializing camera...'}
              </div>

              <Button
                style={{
                  marginTop: 16,
                  display: 'block',
                  margin: '16px auto 0',
                }}
                onClick={stopCamera}
                type="primary"
                danger
              >
                Close Camera
              </Button>
            </div>
          )}
        </Card>

        <Modal
          title="Redeem Prize"
          visible={confirmModal}
          onCancel={() => setConfirmModal(false)}
          className="custom-modal"
          footer={null}
          centered
        >
          <div className="modal-text">Would you like to redeem the prize?</div>
          <div
            style={{
              marginTop: 24,
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
            }}
          >
            <Button
              onClick={handleRedeem}
              className="redeem-modal-button"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Redeem
            </Button>
            <Button
              className="cancel-modal-button"
              onClick={() => setConfirmModal(false)}
            >
              Cancel
            </Button>
          </div>
        </Modal>

        <Modal
          title="Congratulations!"
          visible={successModal}
          className="custom-modal"
          onCancel={() => setSuccessModal(false)}
          footer={null}
          centered
        >
          <p style={{ textAlign: 'center' }}>
            🎉 You have successfully redeemed your prize!
          </p>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button onClick={() => setSuccessModal(false)}>OK</Button>
          </div>
        </Modal>
        <Modal
          title="Redeem Error"
          className="custom-modal"
          visible={errorModal.visible}
          onCancel={() => setErrorModal({ visible: false, message: '' })}
          footer={null}
          centered
        >
          <p style={{ textAlign: 'center' }}>{errorModal.message}</p>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button
              onClick={() => setErrorModal({ visible: false, message: '' })}
            >
              OK
            </Button>
          </div>
        </Modal>
      </div>
    </>
  )
}

export default MafEvents
